import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import type { ChatRoomType } from "@/lib/humhub-api/types";

type KeywordSearchParams = {
  keyword: string;
  type?: ChatRoomType;
};

type UseSearchByKeywordOptions<TResponse> = {
  keyBase: string;
  initialKeyword?: string;
  initialType?: ChatRoomType;
  fetcher: (params: KeywordSearchParams) => Promise<TResponse>;
  staleTimeMs: number;
};

export const useSearchByKeyword = <TResponse>({
  keyBase,
  initialKeyword,
  initialType,
  fetcher,
  staleTimeMs,
}: UseSearchByKeywordOptions<TResponse>) => {
  const [keyword, setKeyword] = useState<string | undefined>(initialKeyword);
  const [type, setType] = useState<ChatRoomType | undefined>(initialType);
  const queryClient = useQueryClient();

  const {
    data: response,
    isLoading,
    error: queryError,
    refetch,
  } = useQuery({
    queryKey: [keyBase, keyword, type],
    queryFn: () => {
      if (!keyword) {
        throw new Error("Search keyword is undefined");
      }
      return fetcher({ keyword, type });
    },
    enabled: !!keyword,
    staleTime: staleTimeMs,
  });

  const search = useCallback(
    async (searchKeyword = initialKeyword, searchType = initialType) => {
      if (!searchKeyword) {
        return null;
      }

      setKeyword(searchKeyword);
      setType(searchType);

      const cachedResult = queryClient.getQueryData([
        keyBase,
        searchKeyword,
        searchType,
      ]);
      if (cachedResult) {
        return cachedResult as TResponse;
      }

      try {
        const result = await queryClient.fetchQuery({
          queryKey: [keyBase, searchKeyword, searchType],
          queryFn: () => fetcher({ keyword: searchKeyword, type: searchType }),
          staleTime: staleTimeMs,
        });
        return result;
      } catch (error) {
        console.error("Search failed:", error);
        return null;
      }
    },
    [initialKeyword, initialType, keyBase, queryClient, fetcher, staleTimeMs],
  );

  const refetchSearch = useCallback(() => {
    if (keyword) {
      refetch();
    }
  }, [keyword, refetch]);

  const prefetchSearch = useCallback(
    (searchKeyword: string, searchType?: ChatRoomType) => {
      queryClient.prefetchQuery({
        queryKey: [keyBase, searchKeyword, searchType],
        queryFn: () => fetcher({ keyword: searchKeyword, type: searchType }),
        staleTime: staleTimeMs,
      });
    },
    [fetcher, keyBase, queryClient, staleTimeMs],
  );

  const clearSearch = useCallback(() => {
    setKeyword(undefined);
    setType(undefined);
  }, []);

  return {
    isLoading,
    error: queryError ? String(queryError) : null,
    search,
    response,
    refetchSearch,
    prefetchSearch,
    clearSearch,
    currentKeyword: keyword,
  };
};
